import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/perms";
import { useTr } from "@/lib/tr";
import api from "@/lib/api";
import { UserAvatar } from "@/components/shared/UserAvatar";
import { changeAvatar } from "@/lib/avatarUpload";
import { Logo } from "@/components/shared/Logo";
import { LanguageSwitcher } from "@/components/shared/LanguageSwitcher";
import { StaffNotifBell } from "@/components/shared/StaffNotifBell";
import { PushToggle } from "@/components/support/PushToggle";
import {
  LayoutDashboard, History, LogOut, Menu, X, UserCircle, Users, LifeBuoy, Server, FileText, ShoppingBag,
  ListOrdered, Workflow, Gauge, Receipt, RotateCcw, CreditCard, Smartphone, Banknote, ScrollText, Activity, KeyRound,
} from "lucide-react";

// Each entry maps to a real TWP page, shown only if the agent holds the permission.
export const AGENT_NAV = [
  { to: "/agent/services", fr: "Services SMM", en: "SMM services", icon: ShoppingBag, perm: "VIEW_SERVICES" },
  { to: "/agent/orders", fr: "Commandes", en: "Orders", icon: ListOrdered, perm: "VIEW_ORDERS" },
  { to: "/agent/providers", fr: "Fournisseurs", en: "Providers", icon: Server, perm: "VIEW_PROVIDERS" },
  { to: "/agent/mapping", fr: "Mapping fournisseurs", en: "Provider mapping", icon: Workflow, perm: "VIEW_PROVIDER_SERVICES" },
  { to: "/agent/support", fr: "Support client", en: "Customer support", icon: LifeBuoy, perm: "VIEW_TICKETS" },
  { to: "/agent/clients", fr: "Clients", en: "Customers", icon: Users, perm: "VIEW_CLIENTS" },
  { to: "/agent/content", fr: "Contenu du site", en: "Site content", icon: FileText, perm: "VIEW_SITE_CONTENT" },
  { to: "/agent/quality", fr: "Qualité des services", en: "Service quality", icon: Gauge, perm: "QUALITY_VIEW" },
  { to: "/agent/team-activity", fr: "Activité des agents", en: "Agents activity", icon: Activity, perm: "VIEW_AGENT_ACTIVITY" },
  { to: "/agent/transactions", fr: "Transactions", en: "Transactions", icon: Receipt, perm: "VIEW_TRANSACTIONS" },
  { to: "/agent/refunds", fr: "Remboursements", en: "Refunds", icon: RotateCcw, perm: "VIEW_REFUNDS" },
  { to: "/agent/payments", fr: "Paiements", en: "Payments", icon: CreditCard, perm: "VIEW_PAYMENTS" },
  { to: "/agent/payment-numbers", fr: "Numéros de paiement", en: "Payment numbers", icon: Smartphone, perm: "VIEW_PAYMENT_NUMBERS" },
  { to: "/agent/partner-withdrawals", fr: "Retraits partenaires", en: "Partner withdrawals", icon: Banknote, perm: "AFFILIATE_PAYOUT_VIEW" },
  { to: "/agent/audit-logs", fr: "Journaux d'audit", en: "Audit logs", icon: ScrollText, perm: "VIEW_AUDIT_LOGS" },
];

function NavItem({ i, active, label, badge, onClick }) {
  return (
    <Link to={i.to} data-testid={`agent-nav-${i.to}`} onClick={onClick}
      className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${active ? "bg-accent text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white"}`}>
      <i.icon className="h-4 w-4 shrink-0" /> <span className="truncate">{label}</span>
      {badge > 0 && <span data-testid="agent-support-badge" className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">{badge}</span>}
    </Link>
  );
}

export function AgentLayout() {
  const { user, logout, setUser } = useAuth();
  const tr = useTr();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [supportUnread, setSupportUnread] = useState(0);
  const canSupport = hasPerm(user, "VIEW_TICKETS");
  useEffect(() => {
    if (!canSupport) return undefined;
    const load = () => api.get("/agent/support/notifications").then((r) => setSupportUnread(r.data.unread || 0)).catch(() => {});
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [canSupport]);
  const active = (to) => loc.pathname === to || loc.pathname.startsWith(to + "/");
  const items = [
    { to: "/agent/dashboard", fr: "Tableau de bord", en: "Dashboard", icon: LayoutDashboard },
    ...AGENT_NAV.filter((i) => hasPerm(user, i.perm)),
    { to: "/agent/history", fr: "Mon historique", en: "My history", icon: History },
    { to: "/agent/password", fr: "Mon mot de passe", en: "My password", icon: KeyRound },
  ];
  return (
    <div className="dark flex min-h-screen bg-slate-950 text-slate-100">
      <aside className={`fixed inset-y-0 left-0 z-40 flex w-64 transform flex-col border-r border-slate-800 bg-slate-900 transition-transform lg:sticky lg:top-0 lg:h-screen lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="flex h-16 shrink-0 items-center justify-between gap-2 border-b border-slate-800 px-5">
          <div className="flex items-center gap-2">
            <Logo alt="Logo" className="h-8 w-8 invert" />
            <div>
              <div className="font-display text-sm font-bold leading-none">The World Promotions</div>
              <div className="mt-0.5 flex items-center gap-1 text-[10px] text-accent"><UserCircle className="h-3 w-3" /> {tr("ESPACE AGENT", "AGENT SPACE")}</div>
            </div>
          </div>
          <button className="lg:hidden" onClick={() => setOpen(false)} data-testid="agent-close-sidebar"><X className="h-5 w-5 text-slate-400" /></button>
        </div>
        <nav className="flex flex-1 flex-col gap-0.5 overflow-y-auto p-3">
          {items.map((i) => <NavItem key={i.to} i={i} active={active(i.to)} label={tr(i.fr, i.en)} badge={i.to === "/agent/support" ? supportUnread : 0} onClick={() => setOpen(false)} />)}
        </nav>
        <button onClick={() => { logout(); nav("/"); }} data-testid="agent-logout-btn"
          className="m-3 flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-slate-400 hover:bg-slate-800 hover:text-white">
          <LogOut className="h-4 w-4" /> {tr("Déconnexion", "Log out")}
        </button>
      </aside>
      {open && <div className="fixed inset-0 z-30 bg-black/60 lg:hidden" onClick={() => setOpen(false)} />}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between gap-3 border-b border-slate-800 bg-slate-900/70 px-5 backdrop-blur-xl">
          <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="agent-open-sidebar"><Menu className="h-6 w-6" /></button>
          <div className="ml-auto flex items-center gap-3">
            <LanguageSwitcher />
            <PushToggle />
            <StaffNotifBell />
            <span className="hidden text-sm text-slate-400 sm:inline" data-testid="agent-header-name">{user?.name} · <span className="text-accent">{user?.job_title}</span></span>
            <button onClick={() => changeAvatar(setUser, (k, f) => f)} data-testid="agent-avatar-btn" className="transition-transform hover:scale-105">
              <UserAvatar user={user} testid="agent-header-avatar" className="h-9 w-9 rounded-full bg-accent text-xs font-bold text-white" />
            </button>
          </div>
        </header>
        <main className="min-w-0 flex-1 p-4 sm:p-5 lg:p-8"><Outlet /></main>
      </div>
    </div>
  );
}
